export interface AircraftQualificationPair {
  aircraftType: string;
  engineType: string;
}

export interface AircraftQualificationPackage {
  id: string;
  label: string;
  qualifications: readonly AircraftQualificationPair[];
}

export const AIRCRAFT_QUALIFICATION_PACKAGES = [
  {
    id: "a320-family-v2500",
    label: "Airbus A320 Family (A319-A320-A321) — IAE V2500-A5",
    qualifications: [
      { aircraftType: "Airbus A319-100 / A319-132", engineType: "IAE V2500-A5" },
      { aircraftType: "Airbus A320-200 (CEO)", engineType: "IAE V2500-A5" },
      { aircraftType: "Airbus A321-200 (CEO)", engineType: "IAE V2500-A5" },
    ],
  },
  {
    id: "a330-200-300-trent-700",
    label: "Airbus A330-200 / A330-300 — Rolls-Royce Trent 700",
    qualifications: [
      { aircraftType: "Airbus A330-200", engineType: "Rolls-Royce Trent 700" },
      { aircraftType: "Airbus A330-300", engineType: "Rolls-Royce Trent 700" },
    ],
  },
  {
    id: "a320-family-cfm56-5b",
    label: "Airbus A320 Family (A318-A319-A320-A321) — CFM56-5B",
    qualifications: [
      { aircraftType: "Airbus A318", engineType: "CFM56-5B" },
      { aircraftType: "Airbus A319-100 / A319-132", engineType: "CFM56-5B" },
      { aircraftType: "Airbus A320-200 (CEO)", engineType: "CFM56-5B" },
      { aircraftType: "Airbus A321-200 (CEO)", engineType: "CFM56-5B" },
    ],
  },
  {
    id: "a330-200-300-pw4000",
    label: "Airbus A330-200 / A330-300 — Pratt & Whitney PW4000",
    qualifications: [
      { aircraftType: "Airbus A330-200", engineType: "Pratt & Whitney PW4000" },
      { aircraftType: "Airbus A330-300", engineType: "Pratt & Whitney PW4000" },
    ],
  },
  {
    id: "a330-200-300-cf6-80e1",
    label: "Airbus A330-200 / A330-300 — GE CF6-80E1",
    qualifications: [
      { aircraftType: "Airbus A330-200", engineType: "GE CF6-80E1" },
      { aircraftType: "Airbus A330-300", engineType: "GE CF6-80E1" },
    ],
  },
  {
    id: "b737-ng-cfm56-7b",
    label: "Boeing 737 NG (737-600/700/800/900) — CFM56-7B",
    qualifications: [
      { aircraftType: "Boeing 737-600", engineType: "CFM56-7B" },
      { aircraftType: "Boeing 737-700", engineType: "CFM56-7B" },
      { aircraftType: "Boeing 737-800", engineType: "CFM56-7B" },
      { aircraftType: "Boeing 737-900ER", engineType: "CFM56-7B" },
    ],
  },
  {
    id: "b777-200-300-ge90",
    label: "Boeing 777-200 / 777-300 — GE90",
    qualifications: [
      { aircraftType: "Boeing 777-200", engineType: "GE90" },
      { aircraftType: "Boeing 777-300ER", engineType: "GE90-115B" },
    ],
  },
  {
    id: "b787-genx-1b",
    label: "Boeing 787 (787-8/787-9/787-10) — GE GEnx-1B",
    qualifications: [
      { aircraftType: "Boeing 787-8", engineType: "GEnx-1B" },
      { aircraftType: "Boeing 787-9", engineType: "GEnx-1B" },
      { aircraftType: "Boeing 787-10", engineType: "GEnx-1B" },
    ],
  },
  {
    id: "gulfstream-gv-sp-br710",
    label: "Gulfstream GV-SP Series — Rolls-Royce BR710",
    qualifications: [
      { aircraftType: "Gulfstream GV-SP Series", engineType: "Rolls-Royce BR710" },
    ],
  },
  {
    id: "b737-max-leap-1b",
    label: "Boeing 737 MAX (737-7/737-8/737-9) — CFM LEAP-1B",
    qualifications: [
      { aircraftType: "Boeing 737 MAX 7", engineType: "CFM LEAP-1B" },
      { aircraftType: "Boeing 737 MAX 8", engineType: "CFM LEAP-1B" },
      { aircraftType: "Boeing 737 MAX 9", engineType: "CFM LEAP-1B" },
    ],
  },
  {
    id: "a320neo-family-pw1100g-jm",
    label: "Airbus A320neo Family (A319neo-A320neo-A321neo) — Pratt & Whitney PW1100G-JM",
    qualifications: [
      { aircraftType: "Airbus A319neo", engineType: "PW1100G-JM" },
      { aircraftType: "Airbus A320neo", engineType: "PW1100G-JM" },
      { aircraftType: "Airbus A321neo", engineType: "PW1100G-JM" },
    ],
  },
  {
    id: "a320neo-family-leap-1a",
    label: "Airbus A320neo Family (A319neo-A320neo-A321neo) — CFM LEAP-1A",
    qualifications: [
      { aircraftType: "Airbus A319neo", engineType: "CFM LEAP-1A" },
      { aircraftType: "Airbus A320neo", engineType: "CFM LEAP-1A" },
      { aircraftType: "Airbus A321neo", engineType: "CFM LEAP-1A" },
    ],
  },
  {
    id: "a350-family-trent-xwb",
    label: "Airbus A350 Family — Rolls-Royce Trent XWB",
    qualifications: [
      { aircraftType: "Airbus A350-900", engineType: "Rolls-Royce Trent XWB-84" },
      { aircraftType: "Airbus A350-1000", engineType: "Rolls-Royce Trent XWB-97" },
      { aircraftType: "Airbus A350F", engineType: "Rolls-Royce Trent XWB-97" },
    ],
  },
] as const satisfies readonly AircraftQualificationPackage[];

export type AircraftQualificationPackageId = typeof AIRCRAFT_QUALIFICATION_PACKAGES[number]["id"];

export const ENGINE_QUALIFICATION_ALIASES: Readonly<Record<string, readonly string[]>> = {
  "IAE V2500-A5": ["V2500", "IAE V2500", "V2500-A5"],
  "PW1100G-JM": ["IAE PW1100G", "PW1100G", "PW1100", "GTF"],
  "CFM LEAP-1A": ["LEAP 1A", "LEAP-1A", "CFM LEAP 1A"],
  "CFM LEAP-1B": ["LEAP 1B", "LEAP-1B", "CFM LEAP 1B"],
  "GE CF6-80E1": ["CF6", "CF6-80E1", "GE CF6"],
  "Pratt & Whitney PW4000": ["PW 4000", "PW4000", "P&W PW4000"],
  "Rolls-Royce Trent 700": ["TRENT700", "TRENT 700", "RR TRENT 700"],
  "GEnx-1B": ["GEnx", "GENX", "GENX-1B", "GE GENX-1B"],
  "GE90": ["GE 90"],
  "Rolls-Royce BR710": ["RRD BR710", "BR710"],
  "Rolls-Royce Trent XWB-84": ["RR Trent XWB", "Rolls-Royce Trent XWB"],
  "Rolls-Royce Trent XWB-97": ["RR Trent XWB", "Rolls-Royce Trent XWB"],
};

export function aircraftQualificationPackage(packageId: string) {
  return AIRCRAFT_QUALIFICATION_PACKAGES.find((item) => item.id === packageId);
}

export function aircraftQualificationPackageForPair(aircraftType: string, engineType: string) {
  return AIRCRAFT_QUALIFICATION_PACKAGES.find((item) =>
    item.qualifications.some((qualification) =>
      qualification.aircraftType === aircraftType && qualification.engineType === engineType));
}

export function aircraftQualificationForPackage(packageId: string, aircraftType?: string) {
  const definition = aircraftQualificationPackage(packageId);
  if (!definition) return null;
  return definition.qualifications.find((qualification) =>
    !aircraftType || qualification.aircraftType === aircraftType) ?? null;
}
